import React from 'react';
import PropTypes from 'prop-types';
import lodash from 'lodash';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Confirm from '../../confirm/Confirm';
import ScheduledTransactionService from '../../../service/ScheduledTransactionService';
import VerticalLabelList from '../../label/VerticalLabelList';
import FieldSet from '../../fieldset/FieldSet';
import ObjectUtils from '../../../modules/ObjectUtils';
import Grid from '../../grid/Grid';
import { cartingServiceScheduledTransaction, paymentScheduledServiceTransaction, } from '../../../modules/Constants';

class DetailedTransaction extends React.Component {
  constructor(props) {
    super(props);
    const { scheduledTransaction, transactionType, } = props;
    this.state = {
      scheduledTransaction,
      transactionType,
      isReady: false,
      isLoading: false,
      processOpen: false,
      cancelOpen: false,
    };

    this.processTransaction = this.processTransaction.bind(this);
    this.cancelTransaction = this.cancelTransaction.bind(this);
    this.handleHide = this.handleHide.bind(this);
    this.csspKeyExclusions = [ 'subPayments', 'subCarts', ];
    this.payableKeyExclusions = [ 'editPayableConversationVersion', 'conditionalNote', ];
  }

  componentDidMount() {
    const { scheduledTransaction, transactionType, } = this.state;
    switch (transactionType) {
      case paymentScheduledServiceTransaction:
        ScheduledTransactionService.getScheduledTransactionDetail(scheduledTransaction.id)
          .then((response) => {
            const { data, } = response;
            this.clearNullValues(data.transaction);
          })
          .catch((err) => {
            throw Error(err);
          });
        break;
      case cartingServiceScheduledTransaction:
        ScheduledTransactionService.getCSSPTransactionDetail(scheduledTransaction.id)
          .then((response) => {
            const { data, } = response;
            this.clearNullValues(data.transaction);
          })
          .catch((err) => {
            throw Error(err);
          });
        break;
      default:
        throw new Error(`Invalid Type: ${transactionType}`);
    }
  }

  handleProcessClickOpen = () => {
    this.setState({ processOpen: true, });
  };

  handleProcessClose = () => {
    this.setState({ processOpen: false, });
  };

  handleCancelClickOpen = () => {
    this.setState({ cancelOpen: true, });
  };

  handleCancelClose = () => {
    this.setState({ cancelOpen: false, });
  };

  clearNullValues(scheduledTransaction) {
    const iterableObject = scheduledTransaction;
    Object.keys(iterableObject).forEach((key) => {
      if (iterableObject[key] === null) {
        iterableObject[key] = '';
      }

      if (typeof iterableObject[key] === 'object') {
        this.clearNullValues(iterableObject[key]);
      }
    });

    this.setState({
      scheduledTransaction: iterableObject,
      isReady: true,
    });
  }

  handleHide() {
    this.setState({ status: '', });
  }

  processTransaction(scheduledTransactionId, transactionType) {
    this.setState({ isLoading: true, });
    function process() {
      if (transactionType === cartingServiceScheduledTransaction) {
        return ScheduledTransactionService.processCSScheduledTransaction({ scheduledTransactionId, });
      } if (transactionType === paymentScheduledServiceTransaction) {
        return ScheduledTransactionService.processScheduledTransaction({ scheduledTransactionId, });
      }
      throw new Error(`Invalid Type: ${transactionType}`);
    }
    process.call()
      .then(() => {
        this.setState({
          status: (
            <Alert className="mt-2" variant="success">
              <Alert.Heading>Success!</Alert.Heading>
              <p>{`Scheduled Transaction ${scheduledTransactionId} has been processed!`}</p>
              <div className="d-flex justify-content-end">
                <Button onClick={this.handleHide} variant="outline-success">
                  OK
                </Button>
              </div>
            </Alert>
          ),
        });
      })
      .catch(() => {
        this.setState({
          status: (
            <Alert className="mt-2" variant="danger">
              <Alert.Heading>Oh snap! You got an error!</Alert.Heading>
              <p>{`Unable to process scheduled transaction ${scheduledTransactionId}`}</p>
              <div className="d-flex justify-content-end">
                <Button onClick={this.handleHide} variant="outline-danger">
                  OK
                </Button>
              </div>
            </Alert>
          ),
        });
      })
      .finally(() => {
        this.handleProcessClose();
        this.setState({ isLoading: false, });
      });
  }

  cancelTransaction(scheduledTransactionId, transactionType) {
    this.setState({ isLoading: true, });
    function cancel() {
      if (transactionType === cartingServiceScheduledTransaction) {
        return ScheduledTransactionService.cancelCSScheduledTransaction({ scheduledTransactionId, });
      } if (transactionType === paymentScheduledServiceTransaction) {
        return ScheduledTransactionService.cancelScheduledTransaction({ scheduledTransactionId, });
      }
      throw new Error(`Invalid Type: ${transactionType}`);
    }
    cancel.call()
      .then(() => {
        this.setState({
          status: (
            <Alert className="mt-2" variant="success">
              <Alert.Heading>Success!</Alert.Heading>
              <p>{`Scheduled Transaction ${scheduledTransactionId} has been cancelled!`}</p>
              <div className="d-flex justify-content-end">
                <Button onClick={this.handleHide} variant="outline-success">
                  OK
                </Button>
              </div>
            </Alert>
          ),
        });
      })
      .catch(() => {
        this.setState({
          status: (
            <Alert className="mt-2" variant="danger">
              <Alert.Heading>Oh snap! You got an error!</Alert.Heading>
              <p>{`Unable to cancel scheduled transaction ${scheduledTransactionId}`}</p>
              <div className="d-flex justify-content-end">
                <Button onClick={this.handleHide} variant="outline-danger">
                  OK
                </Button>
              </div>
            </Alert>
          ),
        });
      })
      .finally(() => {
        this.handleCancelClose();
        this.setState({ isLoading: false, });
      });
  }

  renderPayables(scheduledTransaction, transactionType) {
    // if transaction type isn't in list of types with payables to render return immediately
    if (![ cartingServiceScheduledTransaction, ].includes(transactionType)) {
      return null;
    }
    const payables = [];
    const payableRows = [];

    const subCarts = ObjectUtils.formatStringAsJSONArray(scheduledTransaction.subCarts);
    subCarts.forEach((subcart) => {
      subcart.payables.forEach((payable) => {
        payables.push(payable);
      });
    });

    const formattedPayables = payables;

    formattedPayables.forEach((payable) => {
      const item = payable;
      item.amount = Number(payable.amount?.value ?? payable.amount?.amount).toFixed(2);

      if (payable.minDate !== undefined) {
        item.minDate = new Date(payable.minDate.$date);
      }

      if (payable.maxDate !== undefined) {
        item.maxDate = new Date(payable.maxDate.$date);
      }

      if (payable.createdAt !== undefined) {
        item.createdAt = new Date(payable.createdAt.$date);
      }

      this.payableKeyExclusions.forEach(key => {
        delete item[key];
      });

      return item;
    });

    formattedPayables.forEach(payable => {
      const values = Object.values(payable);
      payableRows.push({
        value: values,
        raw: payable,
      });
    });

    const payableHeaders = ObjectUtils.toLargestKeySet(payables).map(payable => ({
      display: lodash.startCase(payable),
      value: payable,
    }));

    return (
      <FieldSet title="Line Items">
        <Grid headers={payableHeaders} rows={payableRows} />
      </FieldSet>
    );
  }

  renderSummary(scheduledTransaction, transactionType) {
    let exclusions = [];
    switch (transactionType) {
      case cartingServiceScheduledTransaction:
        exclusions = this.csspKeyExclusions;
        break;
      default:
        break;
    }

    const items = VerticalLabelList.toList(scheduledTransaction, {
      displayFormatter: (value) => {
        const formattedValue = value.split('_').join(' ');
        return lodash.startCase(formattedValue);
      },
    });

    return (
      <FieldSet title="Scheduled Transaction Summary" style={{ maxHeight: '400px', }}>
        <VerticalLabelList items={items} exclusions={exclusions} />
      </FieldSet>
    );
  }

  renderActionButtons(isLoading, status, processOpen, cancelOpen, scheduledTransaction, transactionType) {
    const isDeleteEnabled = true;
    const isProcessEnabled = true;
    return (
      <>
        {isProcessEnabled ? [
          <Button
            key={'process-button-0'}
            variant="primary"
            className="me-2 small"
            onClick={this.handleProcessClickOpen}
          >
            Process
          </Button>,
          <Confirm
            key={'process-confirm-0'}
            isLoading={isLoading}
            title="Process Scheduled Transaction Confirmation"
            content="Are you sure you want to process this scheduled transaction?"
            open={processOpen}
            onClose={this.handleProcessClose}
            onYes={() => this.processTransaction(scheduledTransaction.id, transactionType)}
            onNo={this.handleProcessClose}
          />, ] : null}

        {isDeleteEnabled ? [
          <Button key={'delete-button-0'} variant="primary" className="small" onClick={this.handleCancelClickOpen}>
            Cancel
          </Button>,
          <Confirm
            key={'delete-confirm-0'}
            isLoading={isLoading}
            title="Cancel Scheduled Transaction Confirmation"
            content="Are you sure you want to cancel this scheduled transaction?"
            open={cancelOpen}
            onClose={this.handleCancelClose}
            onYes={() => this.cancelTransaction(scheduledTransaction.id, transactionType)}
            onNo={this.handleCancelClose}
          />, ] : null}

        {status}
      </>
    );
  }

  render() {
    const {
      isReady,
      isLoading,
      status,
      scheduledTransaction,
      processOpen,
      cancelOpen,
      transactionType,
    } = this.state;

    return (
      <>
        {(() => {
          if (isReady) {
            return (
              <>
                {this.renderActionButtons(isLoading, status, processOpen, cancelOpen, scheduledTransaction, transactionType)}
                {this.renderSummary(scheduledTransaction, transactionType)}
                {this.renderPayables(scheduledTransaction, transactionType)}
              </>
            );
          }
          return undefined;
        })()}
      </>
    );
  }
}

DetailedTransaction.propTypes = {
  scheduledTransaction: PropTypes.instanceOf(Object).isRequired,
  transactionType: PropTypes.instanceOf(String).isRequired,
};

export default DetailedTransaction;
